import type { CrowdexUIMessage, CrowdexUIMessagePart } from '@crowdex/agent-contracts';
import type {
  ArtifactListingEntry,
  ConversationCard,
  ConversationPlacement,
} from '@crowdex/agent-contracts/api';
import type { HandoffDevice } from '@/components/desktop-shell/continue-locally-dialog';
import type { DesktopPreviewState } from '@/lib/desktop-preview-state';

/**
 * The transcripts of the redesign's transcript and dialog boards (D10–D15, D45–D53), shown by
 * `/desktop-preview/chat?board=<Board>`, and the states each board opens on (a dialog, a menu,
 * offline). Preview only.
 */
export type PreviewBoard = {
  messages: CrowdexUIMessage[];
  preview?: Partial<DesktopPreviewState>;
  /** What the board opens: a dialog, offline, the conversation as a child. */
  open?:
    | 'rewind'
    | 'environment'
    | 'handoff'
    | 'offline'
    | 'child'
    | 'branch'
    | 'artifact'
    | 'cloud-project-missing';
  /** The conversation's name in its header, as the board's (not its first message). */
  title?: string;
  /** Files of the conversation's workspace its replies cite as images, by path (data URLs). */
  workspaceImages?: Record<string, string>;
};

const minutesAgo = (minutes: number) => new Date(Date.now() - minutes * 60_000).toISOString();
const checkpoint = (files: { path: string; additions: number; deletions: number }[]) => ({
  before: 'a'.repeat(40),
  after: 'b'.repeat(40),
  files: files.map((file) => ({ ...file, status: 'modified' as const })),
});

function user(id: string, text: string): CrowdexUIMessage {
  return { id, role: 'user', parts: [{ type: 'text', text }] };
}

function assistant(
  id: string,
  parts: CrowdexUIMessagePart[],
  metadata: Record<string, unknown> = {},
): CrowdexUIMessage {
  return { id, role: 'assistant', parts, metadata } as CrowdexUIMessage;
}

const read = (id: string, path: string): CrowdexUIMessagePart => ({
  type: 'dynamic-tool',
  toolCallId: id,
  toolName: 'read_file',
  title: `Read ${path}`,
  state: 'output-available',
  input: { path },
  output: { lines: 40 },
});

const command = (
  id: string,
  text: string,
  output: string,
  exitCode: number | null = 0,
): CrowdexUIMessagePart[] => [
  {
    type: 'dynamic-tool',
    toolCallId: id,
    toolName: 'exec_command',
    title: text,
    state: exitCode === null ? 'input-available' : 'output-available',
    input: { command: text },
    ...(exitCode === null ? {} : { output: { exitCode } }),
  } as CrowdexUIMessagePart,
  {
    type: 'data-terminal',
    data: {
      toolCallId: id,
      terminalId: `terminal-${id}`,
      output,
      truncated: false,
      exitCode,
      signal: null,
    },
  },
];

/** "Worked 6 min · 9 steps": the CI speed-up turn of D10, D12, D13, D46, D48b, D53. */
function ciSteps(): CrowdexUIMessagePart[] {
  return [
    read('ci-read-1', '.github/workflows/ci.yml'),
    read('ci-read-2', 'Dockerfile'),
    read('ci-read-3', 'package.json'),
    read('ci-read-4', 'pnpm-workspace.yaml'),
    {
      type: 'dynamic-tool',
      toolCallId: 'ci-search',
      toolName: 'web_search',
      title: 'Searched “actions/cache”',
      state: 'output-available',
      input: { query: 'actions/cache' },
      output: { results: 3 },
    },
    {
      type: 'dynamic-tool',
      toolCallId: 'ci-edit',
      toolName: 'apply_patch',
      title: 'Edited ci.yml',
      state: 'output-available',
      input: { path: '.github/workflows/ci.yml' },
      output: { changed: true },
    },
    {
      type: 'data-diff',
      data: {
        toolCallId: 'ci-edit',
        path: '.github/workflows/ci.yml',
        oldText: '- uses: pnpm/action-setup@v4',
        newText: '- uses: actions/cache@v4\n  with:\n    path: ~/.pnpm-store',
      },
    },
    ...command('ci-install', 'pnpm install --frozen-lockfile', 'Done in 18s'),
    {
      type: 'reasoning',
      text: "The Docker build doesn't use layer caching…",
      state: 'done',
      providerMetadata: { crowdex: { durationMs: 12_000 } },
    },
    ...command(
      'ci-test',
      'pnpm --filter web test',
      ' Test Files  48 passed (48)\n      Tests  412 passed (412)\n   Duration  42.10s',
    ),
  ];
}

const ciPlan: CrowdexUIMessagePart = {
  type: 'data-plan',
  data: {
    entries: [
      { content: 'Measure where the CI spends its time', priority: 'high', status: 'completed' },
      { content: 'Find the slowest jobs', priority: 'high', status: 'completed' },
      { content: 'Cache dependencies', priority: 'high', status: 'completed' },
      { content: 'cache the pnpm store between jobs', priority: 'high', status: 'in_progress' },
      { content: 'Cache the Docker layers', priority: 'medium', status: 'pending' },
    ],
  },
};

const ciTimes = { startedAt: minutesAgo(14), finishedAt: minutesAgo(8) };
const ciQuestion = user('ci-user', 'La CI est trop lente, tu peux regarder ?');

const ciDuration: ConversationCard = {
  plugin: 'dashboards',
  id: 'preview-ci-duration',
  afterMessageId: 'ci-reply',
  title: 'CI duration (p50)',
  summary: 'vs 6 min 45 last week · opens in the panel',
  status: 'info',
  widget: { widget: 'dashboards/ci-duration', params: {} },
  // Seconds, read "4 min 12" (D10).
  display: { kind: 'stat', view: null, format: 'duration', better: 'down' },
  createdAt: minutesAgo(8),
  updatedAt: minutesAgo(8),
};

const ciDurationData: DesktopPreviewState['widgetData'] = {
  'dashboards/ci-duration': {
    status: 'ready',
    data: {
      kind: 'series',
      rows: [{ value: 252 }],
      previous: 405,
      trend: [408, 396, 420, 378, 330, 300, 252],
    },
  },
};

function ciTurn(text: string, extra: CrowdexUIMessagePart[] = []): CrowdexUIMessage[] {
  return [
    ciQuestion,
    assistant('ci-reply', [ciPlan, ...ciSteps(), { type: 'text', text }, ...extra], ciTimes),
  ];
}

/** "Working 4 min · 14 steps": the landing page turn of D11, D15. */
function landingTurn(): CrowdexUIMessage[] {
  const subagent = (
    id: string,
    title: string,
    status: 'completed' | 'in_progress',
  ): CrowdexUIMessagePart => ({
    type: 'data-subagent',
    data: {
      toolCallId: id,
      title,
      agentType: 'Claude Code',
      prompt: `${title} for the launch landing page.`,
      status,
      transcript: status === 'completed' ? 'Done.' : '',
      thought: '',
      threadId: null,
      path: null,
      activity: status === 'completed' ? null : 'Checking the hero variants',
      tools: [],
    },
  });
  return [
    user(
      'landing-user',
      "Prépare la landing de lancement : hero, teaser vidéo, et vérifie l'accessibilité.",
    ),
    assistant(
      'landing-reply',
      [
        read('landing-read-1', 'site/src/hero.tsx'),
        read('landing-read-2', 'site/src/theme.ts'),
        ...Array.from({ length: 8 }, (_, index) =>
          read(`landing-read-${index + 3}`, `site/src/section-${index}.tsx`),
        ),
        subagent('landing-copy', 'Write the copy', 'completed'),
        subagent('landing-photos', 'Pick the photos', 'completed'),
        subagent('landing-a11y', 'Check accessibility', 'in_progress'),
        {
          type: 'dynamic-tool',
          toolCallId: 'landing-edit',
          toolName: 'apply_patch',
          title: 'Edited hero.tsx',
          state: 'output-available',
          input: { path: 'site/src/hero.tsx' },
          output: { changed: true },
        },
        {
          type: 'data-diff',
          data: {
            toolCallId: 'landing-edit',
            path: 'site/src/hero.tsx',
            oldText: '<section>\n<h1>Build faster</h1>\n</section>',
            newText: '<section>\n<h1>Ship from anywhere</h1>\n</section>',
          },
        },
        {
          type: 'dynamic-tool',
          toolCallId: 'landing-dev',
          toolName: 'exec_command',
          title: 'pnpm dev',
          state: 'output-available',
          input: { command: 'pnpm dev' },
          output: {},
        },
        {
          type: 'data-terminal',
          data: {
            toolCallId: 'landing-dev',
            terminalId: 'terminal-landing-dev',
            output:
              'Command is running in the background, output is being written to: /tmp/landing-dev.output\n  ➜  Local:   http://localhost:5173/',
            truncated: false,
            exitCode: null,
            signal: null,
          },
        },
        {
          type: 'dynamic-tool',
          toolCallId: 'landing-build',
          toolName: 'exec_command',
          title: 'pnpm build',
          state: 'input-available',
          input: { command: 'pnpm build' },
        },
      ],
      {
        startedAt: minutesAgo(4),
        checkpoint: checkpoint([
          { path: 'site/src/hero.tsx', additions: 2, deletions: 1 },
          { path: 'site/designs/hero/index.html', additions: 40, deletions: 0 },
        ]),
      },
    ),
  ];
}

/** The Design artifact of the landing turn (D11, D15): its hero, three variants. */
const PREVIEW_HERO_ARTIFACT: ArtifactListingEntry = {
  id: 'preview-hero',
  plugin: 'design',
  pluginTitle: 'Design',
  type: 'design/design',
  typeTitle: 'Design',
  space: 'workspace',
  path: 'site/designs/hero',
  conversation: null,
  workspace: null,
  title: 'Hero · 3 variants',
  description: null,
  size: 1,
  modifiedAt: new Date().toISOString(),
  view: null,
  edit: true,
  publish: false,
  publishedRevision: null,
  entry: 'site/designs/hero/index.html',
  template: false,
  duplicate: null,
};

const HERO_HTML = `<!doctype html><html><body style="margin:0;font-family:system-ui;background:#f3efe8;color:#141518;display:grid;place-items:center;min-height:100vh"><div style="max-width:640px;padding:48px"><p style="letter-spacing:.3em;font-weight:700;font-size:12px">CROWDEX</p><h1 style="font-size:56px;line-height:1;margin:12px 0;letter-spacing:-.03em">Ship from anywhere.</h1><p style="font-size:18px;line-height:1.5;color:#3a3b40">Your coding agents keep working in the cloud. Approve, review and merge from your phone.</p><button style="margin-top:16px;border:0;border-radius:999px;background:#141518;color:#fff;padding:14px 24px;font-size:15px;font-weight:600;outline:3px solid #7fb4ff;outline-offset:3px">Get started</button></div></body></html>`;

/** The visual the Designer persona made for the 2.4 announcement (D51). */
const PREVIEW_ANNOUNCE_ARTIFACT: ArtifactListingEntry = {
  ...PREVIEW_HERO_ARTIFACT,
  id: 'preview-announce',
  path: 'site/designs/announce-2-4',
  entry: 'site/designs/announce-2-4/index.html',
  title: 'Annonce 2.4 · 1200 × 630',
};

const ANNOUNCE_HTML = `<!doctype html><html><body style="margin:0;font-family:system-ui;background:#141518;color:#f3efe8;display:grid;place-items:center;min-height:100vh"><div style="padding:40px"><p style="letter-spacing:.3em;font-weight:700;font-size:12px;color:#7fb4ff">CROWDEX 2.4</p><h1 style="font-size:44px;line-height:1.05;margin:10px 0;letter-spacing:-.02em">Mobile drawer. Swipe back. Cloud extensions.</h1><p style="font-size:16px;color:#b9b6b0">And eleven fixes.</p></div></body></html>`;

const groupArtifacts = {
  artifacts: [PREVIEW_ANNOUNCE_ARTIFACT],
  artifactHtml: { [PREVIEW_ANNOUNCE_ARTIFACT.id]: ANNOUNCE_HTML },
};

const landingArtifacts = {
  // The hero, and the announcement visual made in the same conversation (its Summary lists both).
  artifacts: [PREVIEW_HERO_ARTIFACT, PREVIEW_ANNOUNCE_ARTIFACT],
  artifactHtml: {
    [PREVIEW_HERO_ARTIFACT.id]: HERO_HTML,
    [PREVIEW_ANNOUNCE_ARTIFACT.id]: ANNOUNCE_HTML,
  },
  artifactPointed: {
    [PREVIEW_HERO_ARTIFACT.id]: [
      {
        source: 'Design · Hero · 3 variants',
        title: 'button.cta · Variant A',
        selector: 'button.cta',
        html: '<button class="cta">',
        text: 'Get started',
      },
    ],
  },
  artifactVariants: { [PREVIEW_HERO_ARTIFACT.id]: ['A · Bold', 'B · Calm', 'C · Photo'] },
};

const landingCards: ConversationCard[] = [
  {
    plugin: 'video',
    id: 'preview-teaser-render',
    afterMessageId: 'landing-reply',
    title: 'Rendering teaser.mp4',
    summary: '64 % · about 1 min left',
    status: 'running',
    widget: { widget: 'video/render', params: {} },
    display: { kind: 'stat', view: null },
    createdAt: minutesAgo(2),
    updatedAt: minutesAgo(0),
  },
];

/** The mobile inspiration turn of D45, D47, D48, D50: four files changed. */
function inspirationTurn(
  question = "Tu peux regarder les interfaces mobiles de ChatGPT et Claude pour voir comment s'en inspirer ?",
): CrowdexUIMessage[] {
  return [
    user('mobile-user', question),
    assistant(
      'mobile-reply',
      [
        ...Array.from({ length: 12 }, (_, index) =>
          read(`mobile-read-${index}`, `apps/web/src/mobile-${index}.tsx`),
        ),
        {
          type: 'text',
          text: 'Les deux apps convergent : un tiroir à la place de la barre d’onglets, « nouveau » en bas du tiroir, et un ⋮ dans l’en-tête.',
        },
        ...Array.from({ length: 6 }, (_, index) => ({
          type: 'dynamic-tool' as const,
          toolCallId: `mobile-search-${index}`,
          toolName: 'web_search',
          title: `Searched mobile navigation ${index + 1}`,
          state: 'output-available' as const,
          input: { query: 'mobile drawer navigation' },
          output: {},
        })),
        {
          type: 'text',
          text: "J'ai appliqué la proposition A : la barre d'onglets disparaît et More devient le tiroir.",
        },
      ],
      {
        startedAt: minutesAgo(30),
        finishedAt: minutesAgo(26),
        checkpoint: checkpoint([
          {
            path: 'apps/web/src/components/mobile-shell/mobile-shell.tsx',
            additions: 64,
            deletions: 22,
          },
          {
            path: 'apps/web/src/components/mobile-shell/mobile-drawer.tsx',
            additions: 48,
            deletions: 0,
          },
          {
            path: 'apps/web/src/components/mobile-shell/use-swipe-back.ts',
            additions: 6,
            deletions: 4,
          },
          {
            path: 'apps/web/src/components/mobile-shell/activity-model.ts',
            additions: 2,
            deletions: 4,
          },
        ]),
      },
    ),
  ];
}

const swipeApproval = (id: string): CrowdexUIMessagePart =>
  ({
    type: 'dynamic-tool',
    toolCallId: id,
    toolName: 'exec_command',
    title: 'pnpm --filter web test',
    state: 'approval-requested',
    input: { command: 'pnpm --filter web test' },
    approval: { id: `${id}-approval` },
    toolMetadata: { crowdex: { remember: 'Always allow pnpm test here' } },
  }) as CrowdexUIMessagePart;

function swipeTurn(origin: string): CrowdexUIMessage[] {
  return [
    user('swipe-user', origin),
    assistant(
      'swipe-reply',
      [
        read('swipe-read-1', 'apps/web/src/components/mobile-shell/mobile-shell.tsx'),
        read('swipe-read-2', 'apps/web/src/components/mobile-shell/use-swipe-back.ts'),
        {
          type: 'dynamic-tool',
          toolCallId: 'swipe-edit',
          toolName: 'apply_patch',
          title: 'Edited use-swipe-back.ts',
          state: 'output-available',
          input: { path: 'apps/web/src/components/mobile-shell/use-swipe-back.ts' },
          output: {},
        },
        {
          type: 'reasoning',
          text: 'The left edge must not fight the drawer gesture.',
          state: 'done',
        },
        { type: 'step-start' } as CrowdexUIMessagePart,
        {
          type: 'text',
          text: "J'ai ajouté `use-swipe-back.ts`. Je lance les tests pour vérifier.",
        },
        swipeApproval('swipe-test'),
      ],
      { startedAt: minutesAgo(3) },
    ),
  ];
}

const CROWDEX_MOBILE_CONVERSATION = '0b7e3c52-6a8d-4c6f-9a51-2f6d1e0a7b11';

/** The child conversation's placement: task 2 of 5 of Crowdex Mobile, Careful, step Develop. */
export function previewChildPlacements(chatId: string): Record<string, ConversationPlacement> {
  const task = (id: string, step: string | null): [string, ConversationPlacement] => [
    id,
    {
      workspaceId: 'ws-crowlingo',
      projectId: 'proj-mobile',
      parentConversationId: CROWDEX_MOBILE_CONVERSATION,
      workflowRun: {
        workflowId: 'careful',
        automationName: 'Careful',
        definition: '.crowdex/automations/careful.json',
        runId: `run-${id}`,
        step,
        round: null,
        state: 'running',
        costUsd: 0.4,
      },
    },
  ];
  return Object.fromEntries([
    task('preview-task-1', 'Review'),
    task(chatId, 'Develop'),
    task('preview-task-3', 'Develop'),
    task('preview-task-4', null),
    task('preview-task-5', null),
  ]);
}

/** The computers a phone can send the conversation to (mobile 45). */
export const PREVIEW_HANDOFF_DEVICES: readonly HandoffDevice[] = [
  { id: 'macbook', name: 'MacBook Pro', online: true, detail: '~/code/crowdex' },
  { id: 'office', name: 'Office desktop', online: false, detail: 'last seen yesterday' },
];

/** A face drawn as an SVG, turned by `turn` (-1 left, 0 front, 1 right): preview photos. */
function previewFace(turn: number, hue: number): string {
  const x = 60 + turn * 14;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="150" viewBox="0 0 120 150"><rect width="120" height="150" fill="hsl(${hue} 30% 86%)"/><ellipse cx="60" cy="74" rx="36" ry="46" fill="hsl(28 45% 72%)"/><path d="M24 58 Q60 6 96 58 Q80 34 60 36 Q40 34 24 58Z" fill="hsl(45 80% 70%)"/><circle cx="${x - 13}" cy="70" r="3.5" fill="#333"/><circle cx="${x + 13}" cy="70" r="3.5" fill="#333"/><path d="M${x - 4} 78 L${x + turn * 8} 92 L${x + 4} 92" stroke="#7a5a44" fill="none" stroke-width="2"/><path d="M${x - 12} 104 Q${x} 110 ${x + 12} 104" stroke="#7a3f3f" fill="none" stroke-width="3"/></svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

const photoGenerate = (id: string, angle: string, turn: number): CrowdexUIMessagePart[] => [
  {
    type: 'dynamic-tool',
    toolCallId: id,
    toolName: 'crowdex_run',
    title: `avatars.avatar_photo_generate (${angle})`,
    state: 'output-available',
    input: { code: `await avatars.avatar_photo_generate({ reference, angle: '${angle}' })` },
    output: { path: `avatars/trump/photo-${angle}.jpg`, angle },
  },
  {
    type: 'file',
    url: previewFace(turn, 210),
    mediaType: 'image/svg+xml',
    providerMetadata: { crowdex: { toolCallId: id } },
  } as CrowdexUIMessagePart,
];

/** A `crowdex_run` cell of native Claude Code (`mcp__<server>__crowdex_run`), as it streams it. */
const codeRun = (
  id: string,
  code: string,
  result: { value?: unknown; error?: { code: string; message: string } },
): CrowdexUIMessagePart =>
  ({
    type: 'dynamic-tool',
    toolCallId: id,
    toolName: 'mcp__Crowdex_MCP__crowdex_run',
    input: { code },
    ...(result.error === undefined
      ? { state: 'output-available', output: JSON.stringify({ value: result.value }) }
      : { state: 'output-error', errorText: JSON.stringify({ error: result.error }) }),
  }) as CrowdexUIMessagePart;

/** One operation of a cell, the step code mode writes for it (`<namespace>.<tool>`). */
const codeCall = (
  id: string,
  toolName: string,
  input: Record<string, unknown>,
  output: unknown,
  extra: Record<string, unknown> = {},
): CrowdexUIMessagePart =>
  ({
    type: 'dynamic-tool',
    toolCallId: `code-mode-${id}`,
    toolName,
    input,
    state: 'output-available',
    output,
    ...extra,
  }) as CrowdexUIMessagePart;

/** A step whose tool said what it touched (`toolMetadata.crowdex.refs`, as code mode keeps it). */
const touched = (
  id: string,
  toolName: string,
  input: Record<string, unknown>,
  ref: Record<string, unknown>,
): CrowdexUIMessagePart =>
  codeCall(id, toolName, input, { ok: true }, { toolMetadata: { crowdex: { refs: [ref] } } });

/**
 * A turn that read mails, a drive file and a pull request, and handed a review to a task: its
 * previews (three, then "See all · 5") and the Summary's App items, Services and Tasks and runs.
 */
function refsTurn(): CrowdexUIMessage[] {
  const mail = (id: string, title: string, sender: string, text: string) =>
    touched(
      `mail-${id}`,
      'crowbox.get_message',
      { id },
      {
        kind: 'item',
        app: 'com.example.crowbox',
        type: 'mail',
        id,
        title,
        detail: sender,
        action: 'read',
        url: `https://crowbox.example.com/mail?view=all&open=${id}`,
        preview: { text },
      },
    );
  return [
    user('refs-user', 'Rapproche les factures d’octobre du budget et relis la PR de facturation.'),
    assistant(
      'refs-reply',
      [
        mail(
          'm1',
          'Facture Scaleway · octobre',
          'Scaleway',
          'Votre facture de 1 284,00 € est disponible.',
        ),
        mail(
          'm2',
          'Facture OVHcloud · octobre',
          'OVHcloud',
          'Montant : 312,40 € TTC, prélevé le 5 novembre.',
        ),
        touched(
          'drive-budget',
          'drive.read',
          { path: 'Finance/budget-2026.csv' },
          {
            kind: 'item',
            app: 'com.example.crowdrive',
            type: 'file',
            id: 'f-budget',
            title: 'budget-2026.csv',
            detail: 'Finance',
            route: '/files/f-budget',
            action: 'read',
            preview: {
              rows: [
                ['Poste', 'Budget', 'Octobre'],
                ['Cloud', '1 500 €', '1 596 €'],
                ['Outils', '400 €', '388 €'],
              ],
            },
          },
        ),
        touched(
          'github-pr',
          'github.get_pull_request',
          { repository: 'crowlingo/billing', number: 42 },
          {
            kind: 'item',
            app: 'com.example.github',
            type: 'pull-request',
            id: 'crowlingo/billing#42',
            title: 'Rapprochement automatique des factures',
            detail: 'crowlingo/billing #42',
            status: 'open',
            action: 'read',
            url: 'https://github.com/crowlingo/billing/pull/42',
          },
        ),
        touched(
          'github-issue',
          'github.get_issue',
          { repository: 'crowlingo/billing', number: 17 },
          {
            kind: 'item',
            app: 'com.example.github',
            type: 'issue',
            id: 'crowlingo/billing#17',
            title: 'Les avoirs ne sont pas déduits',
            detail: 'crowlingo/billing #17',
            status: 'closed',
            action: 'read',
            url: 'https://github.com/crowlingo/billing/issues/17',
          },
        ),
        touched(
          'task-review',
          'assistant.start_conversation',
          { message: 'Relis la PR #42' },
          {
            kind: 'work',
            app: 'crowdex',
            type: 'task',
            id: 'preview-task-review',
            title: 'Relire la PR #42',
            detail: 'Review',
            status: 'queued',
            action: 'made',
          },
        ),
        { type: 'step-start' } as CrowdexUIMessagePart,
        {
          type: 'text',
          text: 'Le cloud dépasse le budget d’octobre de 96 €. J’ai confié la relecture de la PR #42 à une tâche.',
        },
      ],
      { startedAt: minutesAgo(3), finishedAt: minutesAgo(1) },
    ),
  ];
}

/** The Avatars session run through Crowdex's MCP by Claude Code (the user's capture). */
function codeModeTurn(): CrowdexUIMessage[] {
  const photo = (angle: string) =>
    `avatars.avatar_photo_generate({ reference: 'photos/trump.jpg', angle: '${angle}' })`;
  return [
    user('code-user', 'Fais un avatar de Donald Trump et montre-le moi.'),
    assistant(
      'code-reply',
      [
        codeRun('code-1', 'return await widgets.list();', { value: [] }),
        codeCall('widgets', 'widgets.list', {}, []),
        codeRun(
          'code-2',
          'const { avatars: all } = await avatars.avatar_list({});\nreturn all.map((avatar) => avatar.name);',
          { value: ['Ada', 'Grace'] },
        ),
        codeCall(
          'avatar-list',
          'avatars.avatar_list',
          {},
          { avatars: [{ id: 'ada', name: 'Ada' }] },
          { toolMetadata: { crowdex: { readOnly: true } } },
        ),
        codeRun(
          'code-3',
          "await browser.js({\n  title: 'Open Wikimedia Commons',\n  code: \"await cua.createBrowserTab('iab', 'https://commons.wikimedia.org/wiki/Donald_Trump')\",\n});",
          { value: { tab: 'tab-1' } },
        ),
        codeCall(
          'browser-1',
          'browser.js',
          {
            title: 'Open Wikimedia Commons',
            code: "await cua.createBrowserTab('iab', 'https://commons.wikimedia.org/wiki/Donald_Trump')",
          },
          { tab: 'tab-1' },
        ),
        codeRun(
          'code-4',
          "const shot = await browser.js({ title: 'Read the portrait page', code: 'return await cua.getState();' });\nreturn shot;",
          { value: { url: 'https://commons.wikimedia.org/wiki/Donald_Trump' } },
        ),
        codeCall(
          'browser-2',
          'browser.js',
          { title: 'Read the portrait page', code: 'return await cua.getState();' },
          { url: 'https://commons.wikimedia.org/wiki/Donald_Trump' },
        ),
        {
          type: 'file',
          url: previewFace(0, 120),
          mediaType: 'image/svg+xml',
          providerMetadata: { crowdex: { toolCallId: 'code-4' } },
        } as CrowdexUIMessagePart,
        codeRun(
          'code-5',
          `await Promise.all([\n  ${photo('frontal')},\n  ${photo('left')},\n  ${photo('right')},\n]);`,
          {
            error: {
              code: 'tool_error',
              message: 'The reference photo is too small (512 px at least).',
            },
          },
        ),
        codeRun(
          'code-6',
          "const item = 'donald-trump';\nawait assistant.show({ app: 'Avatars', item });",
          { value: { shown: true } },
        ),
        codeCall(
          'show',
          'assistant.show',
          { app: 'Avatars', item: 'Donald Trump' },
          { shown: true },
          { toolMetadata: { crowdex: { readOnly: true } } },
        ),
        { type: 'step-start' } as CrowdexUIMessagePart,
        { type: 'text', text: 'Voici l’avatar de Donald Trump, ouvert dans Avatars.' },
      ],
      { startedAt: minutesAgo(4), finishedAt: minutesAgo(1) },
    ),
  ];
}

/** The Avatars session: tools return photos, the reply cites the workspace's (D10-D12). */
function avatarTurn(): CrowdexUIMessage[] {
  return [
    user('avatar-user', 'Fais un avatar de trump'),
    assistant(
      'avatar-reply',
      [
        { type: 'text', text: 'Je cherche un portrait de face sur Wikimedia Commons.' },
        read('avatar-read', 'avatars/trump/photo.jpg'),
        ...photoGenerate('avatar-front', 'frontal', 0),
        ...photoGenerate('avatar-left', 'left', 1),
        ...photoGenerate('avatar-right', 'right', -1),
        { type: 'step-start' } as CrowdexUIMessagePart,
        {
          type: 'text',
          text: [
            'Voici les photos envoyées à Avaturn :',
            '',
            '![Face](avatars/trump/photo-frontal.jpg) ![Profil gauche](avatars/trump/photo-left.jpg) ![Profil droit](./avatars/trump/photo-right.jpg)',
            '',
            'Une photo hors de l’espace de travail ne s’affiche pas : ![Secret](../secrets/key.png)',
          ].join('\n'),
        },
      ],
      { startedAt: minutesAgo(6), finishedAt: minutesAgo(2) },
    ),
  ];
}

/** A turn still running (preview only): its live activity, from sent to its last step. */
const avatarsQuestion = user('avatars-user', 'Fais-moi trois avatars pour l’équipe support.');
function avatarsTurn(extra: CrowdexUIMessagePart[] = []): CrowdexUIMessage[] {
  return [
    avatarsQuestion,
    assistant(
      'avatars-reply',
      [
        {
          type: 'reasoning',
          text: 'Three support avatars: the Avatars skill does it.',
          state: 'done',
        },
        {
          type: 'dynamic-tool',
          toolCallId: 'avatars-skill',
          toolName: 'Skill',
          title: 'Skill Avatars',
          state: 'output-available',
          input: { skill: 'avatars' },
          output: {},
        } as CrowdexUIMessagePart,
        read('avatars-read', 'skills/avatars/SKILL.md'),
        { type: 'step-start' } as CrowdexUIMessagePart,
        { type: 'text', text: "J'utilise le skill Avatars." },
        ...extra,
      ],
      { startedAt: minutesAgo(1) },
    ),
  ];
}

const BOARDS: Record<string, () => PreviewBoard> = {
  TurnSubmitted: () => ({
    messages: [avatarsQuestion],
    preview: { chatStatus: 'submitted' },
  }),
  TurnThinking: () => ({
    messages: [avatarsQuestion, assistant('avatars-reply', [], { startedAt: minutesAgo(0) })],
    preview: { chatStatus: 'streaming' },
  }),
  // Its words written, the next step not begun yet (the user's capture: "Planning next moves").
  TurnRunning: () => ({
    messages: avatarsTurn([{ type: 'step-start' } as CrowdexUIMessagePart]),
    preview: { chatStatus: 'streaming' },
  }),
  TurnTool: () => ({
    messages: avatarsTurn(command('avatars-render', 'pnpm avatars render --count 3', '', null)),
    preview: { chatStatus: 'streaming' },
  }),
  TurnDone: () => ({ messages: avatarsTurn() }),
  DeskCards: () => ({
    messages: ciTurn(
      'Le cache du store pnpm fait gagner un tiers du temps. Il reste la construction de l’image Docker.',
    ),
    preview: { cards: [ciDuration], widgetData: ciDurationData },
  }),
  DeskCardsMore: () => ({
    messages: landingTurn(),
    preview: { cards: landingCards, ...landingArtifacts },
  }),
  DeskSteps: () => ({
    messages: ciTurn('Le cache du store pnpm fait gagner un tiers du temps.'),
  }),
  // A turn that writes between its steps (design D12): each run of steps stays where it ran,
  // between the words around it, while the turn works.
  DeskStepsLive: () => ({
    messages: [
      ciQuestion,
      assistant(
        'ci-reply',
        [
          { type: 'text', text: 'Je regarde d’abord le workflow et le Dockerfile.' },
          read('ci-read-1', '.github/workflows/ci.yml'),
          read('ci-read-2', 'Dockerfile'),
          { type: 'step-start' } as CrowdexUIMessagePart,
          {
            type: 'text',
            text: 'Le store pnpm n’est pas mis en cache. Je lance les tests pour mesurer.',
          },
          { type: 'reasoning', text: 'The cache key should hash the lockfile.', state: 'done' },
          ...command('ci-test', 'pnpm test', 'Tests  412 passed (412)\n Duration  42.10s'),
          { type: 'step-start' } as CrowdexUIMessagePart,
          { type: 'text', text: 'Les tests passent en 42 s. J’ajoute le cache' },
        ],
        { startedAt: minutesAgo(3) },
      ),
    ],
    preview: { chatStatus: 'streaming' },
  }),
  DeskQuestion: () => ({
    messages: [
      ciQuestion,
      assistant(
        'ci-reply',
        [
          ...ciSteps(),
          { type: 'step-start' } as CrowdexUIMessagePart,
          {
            type: 'text',
            text: "Le cache du store pnpm fait gagner un tiers du temps. Pour la suite, il y a deux pistes pour l'image Docker.",
          },
          {
            type: 'dynamic-tool',
            toolCallId: 'ci-question',
            toolName: 'request_user_input',
            title: 'Docker build',
            state: 'input-available',
            input: {
              questions: [
                {
                  header: 'Docker build',
                  question: 'How should I speed up the Docker build?',
                  options: [
                    {
                      label: 'Layer cache in the registry',
                      description: '~2 min saved, no workflow change',
                      recommended: true,
                    },
                    { label: 'Build only on main', description: 'Fastest PRs, image tested later' },
                  ],
                },
                {
                  header: 'Cache scope',
                  question: 'Should the cache be shared between branches?',
                  options: [{ label: 'Yes, share it' }, { label: 'No, one per branch' }],
                },
              ],
            },
          } as CrowdexUIMessagePart,
        ],
        ciTimes,
      ),
    ],
  }),
  DeskApproval: () => ({
    messages: swipeTurn('Ajoute le retour par glissement depuis le bord gauche.'),
  }),
  DeskArtifact: () => ({
    messages: landingTurn(),
    preview: { cards: landingCards, ...landingArtifacts },
    open: 'artifact',
  }),
  DeskBranch: () => ({ messages: inspirationTurn(), open: 'branch', preview: { branchBehind: 5 } }),
  DeskRewind: () => ({
    messages: [
      ...ciTurn(
        'Le cache du store pnpm fait gagner un tiers du temps. Il reste la construction de l’image Docker.',
      ),
      user('ci-user-2', 'Et le build Docker ?'),
      assistant('ci-reply-2', [{ type: 'text', text: 'Je regarde le Dockerfile.' }]),
      user('ci-user-3', 'Ajoute aussi le cache des layers.'),
      assistant('ci-reply-3', [{ type: 'text', text: 'C’est fait.' }]),
    ],
    open: 'rewind',
    preview: { cards: [ciDuration], widgetData: ciDurationData },
  }),
  DeskEnvironment: () => ({ messages: inspirationTurn(), open: 'environment' }),
  DeskHandoff: () => ({ messages: inspirationTurn(), open: 'handoff' }),
  // The Summary of a cloud conversation, with its repository or in an empty sandbox (D5).
  DeskSummaryCloud: () => ({ messages: inspirationTurn(), preview: { inCloud: true } }),
  DeskSummarySandbox: () => ({ messages: inspirationTurn(), preview: { emptySandbox: true } }),
  DeskHandoffAsk: () => ({
    messages: ciTurn(
      'Le cache du store pnpm fait gagner un tiers du temps. Il reste la construction de l’image Docker.',
      [
        { type: 'step-start' } as CrowdexUIMessagePart,
        {
          type: 'dynamic-tool',
          toolCallId: 'ci-handoff',
          toolName: 'continue_on_this_computer',
          title: 'Continue on this computer',
          state: 'approval-requested',
          input: {
            reason:
              'I need your local database to test the migration. The conversation moves to a worktree on this MacBook.',
          },
          approval: { id: 'ci-handoff-approval' },
        } as CrowdexUIMessagePart,
      ],
    ),
    preview: { cards: [ciDuration], widgetData: ciDurationData },
  }),
  DeskElicitation: () => ({
    messages: [
      ...inspirationTurn(),
      assistant('triage-reply', [
        {
          type: 'data-elicitation',
          data: {
            requestId: 'triage-elicitation',
            sessionId: null,
            toolCallId: null,
            message: 'To create the issue the agent asked for',
            mode: 'form',
            requestedSchema: {
              type: 'object',
              title: 'Linear',
              properties: {
                team: {
                  type: 'string',
                  title: 'Team',
                  enum: ['Mobile', 'Web', 'Platform', 'Design', 'Infra'],
                  default: 'Mobile',
                },
                priority: {
                  type: 'string',
                  title: 'Priority',
                  enum: ['Low', 'Medium', 'High', 'Urgent'],
                  default: 'High',
                },
                assignee: {
                  type: 'string',
                  title: 'Assignee',
                  enum: ['Jonas', 'Léa', 'Marc', 'Sami', 'Inès'],
                },
              },
              required: ['team', 'priority'],
            },
            status: 'pending',
          },
        } as CrowdexUIMessagePart,
      ]),
    ],
  }),
  DeskGroup: () => ({
    messages: [
      user(
        'notes-user',
        'Rédige les notes de version de la 2.4 à partir des PR mergées depuis la 2.3.',
      ),
      assistant('notes-reply', [
        {
          type: 'text',
          text: 'Voilà le brouillon : trois nouveautés (tiroir mobile, retour par glissement, extensions cloud) et onze corrections. Je l’ai mis dans RELEASE_NOTES.md.',
        },
      ]),
      user(
        'notes-persona',
        '@[Designer](persona:designer) tu peux proposer un visuel pour l’annonce ?',
      ),
      assistant(
        'notes-designer',
        [
          {
            type: 'text',
            text: 'Un visuel au format 1200 × 630 qui reprend les trois points des notes, aux couleurs du site.',
          },
        ],
        {
          persona: { id: 'designer', name: 'Designer', agentName: 'Claude Code' },
          checkpoint: checkpoint([
            { path: 'site/designs/announce-2-4/index.html', additions: 24, deletions: 0 },
          ]),
        },
      ),
    ],
    preview: groupArtifacts,
  }),
  DeskOffline: () => ({
    messages: [
      ciQuestion,
      assistant('ci-reply', [
        { type: 'text', text: 'Le cache du store pnpm fait gagner un tiers du temps.' },
      ]),
    ],
    open: 'offline',
    preview: { turnFailure: 'Anthropic API error 529: overloaded_error', inCloud: true },
  }),
  DeskImages: () => ({
    messages: avatarTurn(),
    workspaceImages: {
      'avatars/trump/photo-frontal.jpg': previewFace(0, 30),
      'avatars/trump/photo-left.jpg': previewFace(1, 30),
      'avatars/trump/photo-right.jpg': previewFace(-1, 30),
    },
  }),
  DeskCodeMode: () => ({ messages: codeModeTurn() }),
  // A new conversation in a folder Crowdex Cloud has no copy of: its ways on, not an error.
  DeskCloudProjectMissing: () => ({ messages: [], open: 'cloud-project-missing' }),
  // What a conversation touched: previews in the message, rows in the Summary.
  DeskRefs: () => ({ messages: refsTurn() }),
  DeskSubagent: () => ({
    messages: swipeTurn(
      'Add swipe-back from the left edge on every screen with a back button. Small PR, lint first (see PREFERENCES.md).',
    ),
    open: 'child',
  }),
};

/** The mobile boards show the same transcripts as their desktop boards. */
const MOBILE_BOARDS: Record<string, string> = {
  Cards: 'DeskCards',
  CardsMore: 'DeskCardsMore',
  StepsSheet: 'DeskSteps',
  QuestionDock: 'DeskQuestion',
  ApprovalDock: 'DeskApproval',
  ArtifactView: 'DeskArtifact',
  BranchSheet: 'DeskBranch',
  Rewind: 'DeskRewind',
  Environment: 'DeskEnvironment',
  Handoff: 'DeskHandoff',
  Elicitation: 'DeskElicitation',
  Group: 'DeskGroup',
  Offline: 'DeskOffline',
  Subagent: 'DeskSubagent',
  Images: 'DeskImages',
  CodeMode: 'DeskCodeMode',
};

/** The board `?board=` names on a `/desktop-preview` page, or null. */
export function previewBoardName(location: Pick<Location, 'pathname' | 'search'>): string | null {
  if (!location.pathname.startsWith('/desktop-preview')) return null;
  const name = new URLSearchParams(location.search).get('board');
  if (name === null) return null;
  const desktop = MOBILE_BOARDS[name] ?? name;
  return desktop in BOARDS ? desktop : null;
}

/** Each board's conversation, named as on the board. */
const BOARD_TITLES: Record<string, string> = {
  DeskCards: 'Accélération CI',
  DeskCardsMore: 'Landing de lancement',
  DeskSteps: 'Accélération CI',
  DeskStepsLive: 'Accélération CI',
  DeskQuestion: 'Accélération CI',
  DeskApproval: 'Navigation swipe gestures',
  DeskArtifact: 'Landing de lancement',
  DeskBranch: 'Mobile interface inspiration',
  DeskRewind: 'Accélération CI',
  DeskEnvironment: 'Mobile interface inspiration',
  DeskHandoff: 'Mobile interface inspiration',
  DeskSummaryCloud: 'Mobile interface inspiration',
  DeskSummarySandbox: 'Mobile interface inspiration',
  DeskHandoffAsk: 'Accélération CI',
  DeskElicitation: 'Triage new issues',
  DeskGroup: 'Release notes 2.4',
  DeskOffline: 'Accélération CI',
  DeskSubagent: 'Navigation swipe gestures',
  DeskImages: 'Avatar de Trump',
  DeskCodeMode: 'Avatar de Donald Trump',
  DeskRefs: 'Factures d’octobre',
};

export function previewBoard(name: string | null): PreviewBoard | null {
  const board = name === null ? undefined : BOARDS[name]?.();
  if (board === undefined || name === null) return null;
  const title = BOARD_TITLES[name];
  return title === undefined ? board : { title, ...board };
}

/** The conversation's name on the board the preview page shows, if any. */
export function previewBoardTitle(): string | null {
  if (typeof window === 'undefined') return null;
  return previewBoard(previewBoardName(window.location))?.title ?? null;
}

/** The workspace images of the board the preview page shows, read as its replies cite them. */
export function previewWorkspaceImages(): {
  key: string;
  root: null;
  load: (path: string) => Promise<string>;
} | null {
  if (typeof window === 'undefined') return null;
  const name = previewBoardName(window.location);
  const images = previewBoard(name)?.workspaceImages;
  if (images === undefined || name === null) return null;
  return {
    key: `preview:${name}`,
    root: null,
    load: async (path) => {
      const url = images[path];
      if (url === undefined) throw new Error('No such file.');
      return url;
    },
  };
}
